import React from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { SectionCard } from './SectionCard';
import { CPD_FLAG_META, flagLabel } from '../lnd/cpdFlags';
import { formatDateTime } from '../../utils/dateFormat';

// The spot-check flags a declaration filed with (docs/modules/workflow-ld-cpd.md
// §4): one row per finding, each confirmed on its own. Shared by the
// declaration page and HR's modal in Management → Training Records.
//
//   flags      — the declaration's `flags`
//   canConfirm — the viewer holds the spot-check key and the declaration is filed
//   onConfirm  — async (codes[]) => void; [] = every unconfirmed flag
//   busy       — disables the buttons while a confirm is in flight
//   bare       — no SectionCard frame (inside a modal)
const CpdFlagsCard = ({ flags = [], canConfirm = false, onConfirm = null, busy = false, bare = false }) => {
  if (flags.length === 0) return null;
  const open = flags.filter((f) => !f.confirmedAt);

  const body = (
    <>
      <ul className="divide-y divide-slate-100">
        {flags.map((f) => (
          <li key={f.code} className="py-2.5 flex items-start gap-3">
            {f.confirmedAt
              ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-slate-400" />
              : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />}
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-medium ${f.confirmedAt ? 'text-slate-600' : 'text-amber-900'}`}>
                {flagLabel(f.code)}
                {CPD_FLAG_META[f.code] && (
                  <span className="ml-1.5 font-normal text-xs text-slate-500">{CPD_FLAG_META[f.code].hint}</span>
                )}
              </p>
              <p className="text-xs text-slate-600 mt-0.5 break-words">{f.detail}</p>
              {f.confirmedAt && (
                <p className="text-xs text-slate-500 mt-0.5">
                  Confirmed by {f.confirmedBy || 'HR'} on {formatDateTime(f.confirmedAt)}
                </p>
              )}
            </div>
            {canConfirm && onConfirm && !f.confirmedAt && (
              <button
                type="button"
                onClick={() => onConfirm([f.code])}
                disabled={busy}
                className="shrink-0 px-2.5 py-1 text-xs font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Confirm
              </button>
            )}
          </li>
        ))}
      </ul>
      {canConfirm && onConfirm && open.length > 1 && (
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={() => onConfirm([])}
            disabled={busy}
            className="px-3 py-1.5 text-xs font-medium rounded-md bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50"
          >
            Confirm All
          </button>
        </div>
      )}
    </>
  );

  if (bare) return body;
  return (
    <SectionCard
      title="Spot-check Flags"
      count={open.length > 0 ? `${open.length} to confirm` : 'all confirmed'}
    >
      {body}
    </SectionCard>
  );
};

export default CpdFlagsCard;
